import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { AiPromptSandbox } from './AiPromptSandbox';

type PromptKey = 'semantic' | 'performance' | 'compliance' | 'synthesis' | 'recordSummary';

interface PromptsResponse {
  overrides: Record<PromptKey, string>;
  defaults: Record<PromptKey, string>;
}

const PROMPT_LABELS: Record<PromptKey, { title: string; subtitle: string }> = {
  semantic: {
    title: 'Pass 1 — Semantic extraction',
    subtitle: 'Extracts objective facts, intents, objections, buying signals, quantities, deadlines, competitors. The foundation pass — downstream passes consume its output.',
  },
  performance: {
    title: 'Pass 2 — Agent performance',
    subtitle: 'Scores discovery / qualification / product fit / objection handling / close on a 5-axis scale. Produces strengths, improvement areas, missed signals, coaching priority.',
  },
  compliance: {
    title: 'Pass 3 — Compliance',
    subtitle: 'Checks calls against the rules in the Sales playbook. Skipped automatically when no rules are configured. Surfaces violations + incident-recommended flag.',
  },
  synthesis: {
    title: 'Pass 4 — Executive synthesis',
    subtitle: 'Composes the executive summary, deal snapshot, recommended next action, CRM task, executive action, and risk flags from passes 1–3.',
  },
  recordSummary: {
    title: 'Pass 5 — Rolling record summary',
    subtitle: 'After each call analysis, rolls all the record\'s prior calls into a 3–6 sentence narrative. Drives the lead/customer/contact/cold-lead detail view.',
  },
};

const PROMPT_ORDER: PromptKey[] = ['semantic', 'performance', 'compliance', 'synthesis', 'recordSummary'];

// Effective prompt = saved override if non-empty, else the baked-in default.
// We seed the textareas with this so admins can read+edit the live prompt
// directly instead of looking at a blank box.
function effectivePrompt(data: PromptsResponse | undefined, key: PromptKey): string {
  if (!data) return '';
  const ovr = data.overrides[key];
  if (typeof ovr === 'string' && ovr.trim().length > 0) return ovr;
  return data.defaults[key] ?? '';
}

export function AiPromptsSection(): JSX.Element {
  const qc = useQueryClient();
  const promptsQuery = useQuery({
    queryKey: ['system-config-ai-prompts'],
    queryFn: () => api.get<PromptsResponse>('/system-config/ai/prompts'),
  });

  const [drafts, setDrafts] = useState<Record<PromptKey, string>>({
    semantic: '', performance: '', compliance: '', synthesis: '', recordSummary: '',
  });
  const [showDefault, setShowDefault] = useState<Record<PromptKey, boolean>>({
    semantic: false, performance: false, compliance: false, synthesis: false, recordSummary: false,
  });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Reload drafts whenever the server response changes — every textarea
  // shows the current effective prompt (override OR default).
  useEffect(() => {
    if (!promptsQuery.data) return;
    const initial: Record<PromptKey, string> = { semantic: '', performance: '', compliance: '', synthesis: '', recordSummary: '' };
    for (const key of PROMPT_ORDER) {
      initial[key] = effectivePrompt(promptsQuery.data, key);
    }
    setDrafts(initial);
  }, [promptsQuery.data]);

  // Per-pass: is there a saved override on the server right now?
  function isSavedOverride(key: PromptKey): boolean {
    return (promptsQuery.data?.overrides[key] ?? '').trim().length > 0;
  }

  // Per-pass: does the textarea match the baked-in default exactly?
  function matchesDefault(key: PromptKey): boolean {
    return drafts[key] === (promptsQuery.data?.defaults[key] ?? '');
  }

  function handleEdit(key: PromptKey) {
    return (e: React.ChangeEvent<HTMLTextAreaElement>) =>
      setDrafts((d) => ({ ...d, [key]: e.target.value }));
  }

  function handleResetToDefault(key: PromptKey) {
    if (!promptsQuery.data) return;
    setDrafts((d) => ({ ...d, [key]: promptsQuery.data!.defaults[key] }));
  }

  async function handleSave(): Promise<void> {
    if (!promptsQuery.data) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      // For each pass: if the textarea matches the default exactly, send
      // an empty string (server deletes the Setting row → revert to
      // default). Otherwise persist the value as the override.
      const payload: Partial<Record<PromptKey, string>> = {};
      for (const key of PROMPT_ORDER) {
        payload[key] = matchesDefault(key) ? '' : drafts[key];
      }
      await api.patch('/system-config/ai/prompts', payload);
      qc.invalidateQueries({ queryKey: ['system-config-ai-prompts'] });
      setSaveMsg({ ok: true, text: 'Saved. Changes take effect on the next analysis.' });
    } catch {
      setSaveMsg({ ok: false, text: 'Save failed. Please try again.' });
    } finally {
      setSaving(false);
    }
  }

  // "Dirty" = current draft differs from the live effective prompt on the
  // server (i.e. saving would produce a different effective prompt than
  // what's running right now).
  const dirty = useMemo(() => {
    if (!promptsQuery.data) return false;
    return PROMPT_ORDER.some((k) => drafts[k] !== effectivePrompt(promptsQuery.data, k));
  }, [drafts, promptsQuery.data]);

  return (
    <section className="card p-4 space-y-4">
      <header className="flex items-center justify-between border-b border-ink-200 pb-3">
        <div>
          <h2 className="text-sm font-semibold text-ink-900">AI prompts</h2>
          <p className="text-xs text-ink-500 mt-0.5">
            Each editor shows the live prompt (your override if you set one, otherwise the baked-in default).
            Edits take effect on the next worker tick — no restart required. Click "Reset to default" to undo
            a saved override.
          </p>
        </div>
      </header>

      {promptsQuery.isLoading && <p className="text-sm text-ink-400">Loading…</p>}

      <div className="space-y-3">
        {PROMPT_ORDER.map((key) => {
          const label = PROMPT_LABELS[key];
          const draft = drafts[key];
          const def = promptsQuery.data?.defaults[key] ?? '';
          const overridden = isSavedOverride(key);
          return (
            <details
              key={key}
              className="rounded border border-ink-200 bg-white"
            >
              <summary className="cursor-pointer px-3 py-2 text-sm flex items-center justify-between">
                <span>
                  <span className="font-medium">{label.title}</span>
                  {overridden ? (
                    <span className="ml-2 inline-flex items-center rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                      OVERRIDDEN
                    </span>
                  ) : (
                    <span className="ml-2 inline-flex items-center rounded bg-ink-100 px-1.5 py-0.5 text-[10px] font-medium text-ink-600">
                      DEFAULT
                    </span>
                  )}
                </span>
                <span className="text-[11px] text-ink-500">
                  {draft.length.toLocaleString()} chars
                </span>
              </summary>
              <div className="border-t border-ink-100 p-3 space-y-2">
                <p className="text-[11px] text-ink-500">{label.subtitle}</p>
                <textarea
                  className="input font-mono text-xs leading-relaxed"
                  rows={14}
                  value={draft}
                  onChange={handleEdit(key)}
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    className="btn-secondary !px-2 !py-1 text-xs"
                    onClick={() => handleResetToDefault(key)}
                    title="Replace the editor with the baked-in default. Saving will clear any override."
                    disabled={draft === def}
                  >
                    Reset to default
                  </button>
                  <button
                    type="button"
                    className="btn-secondary !px-2 !py-1 text-xs"
                    onClick={() => setShowDefault((s) => ({ ...s, [key]: !s[key] }))}
                  >
                    {showDefault[key] ? 'Hide baked-in default' : 'Show baked-in default'}
                  </button>
                  {overridden && draft !== (promptsQuery.data?.overrides[key] ?? '') && (
                    <span className="text-[11px] text-amber-700">Unsaved edits to override</span>
                  )}
                </div>
                {showDefault[key] && (
                  <pre className="rounded bg-ink-50 p-2 text-[11px] leading-relaxed font-mono whitespace-pre-wrap max-h-72 overflow-y-auto">
                    {def}
                  </pre>
                )}
              </div>
            </details>
          );
        })}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          className="btn-primary"
          onClick={() => void handleSave()}
          disabled={saving || !dirty}
        >
          {saving ? 'Saving…' : 'Save prompts'}
        </button>
        {!dirty && !saving && <span className="text-xs text-ink-500">No unsaved changes.</span>}
        {saveMsg && (
          <p className={`text-sm ${saveMsg.ok ? 'text-emerald-600' : 'text-red-600'}`}>
            {saveMsg.text}
          </p>
        )}
      </div>

      <AiPromptSandbox />
    </section>
  );
}
